import {useState, useEffect, useContext} from "react";
import styles from "../ExportSheet/ExportSheetModal.module.css";
import CloseModalIcon from "../../../assets/closeModalIcon.svg";
import {getStoresFromDatabase} from "../../../hooks/Store";
import {exportProducts} from "../../../hooks/Product";
import {exportExpiredProducts} from "../../../hooks/ExpiredProduct";
import * as XLSX from "xlsx";
import {StoresContext} from '../../../context/StoresContext';
import {ExpiredProductContext} from '../../../context/ExpiredProductContext';
import {FilterExpiredProductContext} from '../../../context/FilterExpiredProductContext';
import {TransformDateToBrazilianFormat} from "../../../utils/TransformDateToBrazilianFormat";
import {ToastContainer, toast} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {ADMIN_EXPIRY_DATE_CONTROL_ROUTE,} from '../../../utils/Constants';

function Modal({ closeModal }) {
  const [selectedStoreState, setSelectedStoreState] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [stores, setStores] = useState([]);
  const [progress, setProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const {selectedStore} = useContext(StoresContext);
  const {expiredFilteredProducts} = useContext(ExpiredProductContext);
  const {isFilter} = useContext(FilterExpiredProductContext);

  useEffect(() => {
    if(selectedStore === null || selectedStore === undefined) {
      const fetchStores = async () => {
        const storeData = await getStoresFromDatabase();
        if (storeData) {
          setStores(storeData.data.stores);
        }
      };
      fetchStores();
    }else {
      setSelectedStoreState(selectedStore)
    }
  }, []);

  const handleStoreSelection = (store) => {
    setSelectedStoreState(store);
  };

  const exportData = async () => {
    if (!selectedStoreState) {
      toast.error("Selecione uma loja.");
      return;
    }
    setIsProcessing(true);
    setProgress(0);
    try {
      const productsData = await exportProducts([selectedStoreState.id])
      if(productsData) {
        const { products } = productsData.data;
        if (!products || products.length === 0) {
          toast.error("Nenhum produto encontrado para esta loja.");
          setIsProcessing(false);
          return;
        }
        await generateExcelForStore(selectedStoreState.name, products);
        setProgress(100);
        toast.success("Exportação concluída com sucesso!");
      }
    } catch (error) {
      console.error("Erro ao exportar planilha:", error);

    } finally {
      setIsProcessing(false);
    } 
  };

  const exportExpiredProductsData = async () => {
    if (!selectedStoreState) {
      toast.error("Selecione uma loja.");
      return;
    }
    setIsProcessing(true);
    setProgress(0);
    try {
      const expiredProductData = await exportExpiredProducts([selectedStoreState.id])
      if( expiredProductData ) {
        const { expiredProducts } = expiredProductData.data;
        if (!expiredProducts || expiredProducts.length === 0) {
          toast.error("Nenhum produto encontrado para esta loja.");
          setIsProcessing(false);
          return;
        }
        await generateExpiredProductExcel(selectedStoreState.name, expiredProducts);
        setProgress(100);
        toast.success("Exportação concluída com sucesso!");
      }
    } catch (error) {
      console.error("Erro ao exportar planilha:", error);

    } finally {
      setIsProcessing(false);
    } 
  };

  const exportExpiredFilteredProducts = async () => {
    if (!expiredFilteredProducts) {
      toast.error("Sem filtros.");
      return;
    }
    setIsProcessing(true);
    setProgress(0);
    try {
      const response = await generateExpiredProductExcel(selectedStoreState.name, expiredFilteredProducts);
      setProgress(100);
      toast.success("Exportação concluída com sucesso!");
    } catch (error) {
      console.error("Erro ao exportar planilha:", error);
    }finally {
      setIsProcessing(false);
    }
  }

  const onExport = () => {
    if(isFilter) {
      exportExpiredFilteredProducts()
    }else {
      if(isControleValidadeRoute) {
        exportExpiredProductsData()
      }else {
        exportData()
      }
    }
  }

  const generateExcelForStore = async ( storeName, products ) => {
    try {
      const formattedProducts = products.map((product) => ({
        "Id": product.id,
        "Código de Barras": product.barcode,
        "Nome do Produto": product.name,
        "Fabricante": product.manufacturer,
        "Grupo": product.group,
      }));
      const worksheet = XLSX.utils.json_to_sheet(formattedProducts);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Produtos");
      const excelData = XLSX.write(workbook, { type: "array", bookType: "xlsx" });
      const blob = new Blob([excelData], { type: "application/octet-stream" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Produtos_${storeName}.xlsx`;
      link.click();
      console.log(`Planilha gerada com sucesso para a loja: ${storeName}`);
    } catch (error) {
      console.error("Erro ao gerar a planilha:", error);
      toast.error("Erro ao gerar a planilha. Tente novamente.");
    }
  };

  const generateExpiredProductExcel = async ( storeName, expiredProducts ) => {
    try {
      const formattedProducts = expiredProducts.map((expiredProduct) => ({
        "Id": expiredProduct.product.id,
        "Código de Barras": expiredProduct.product.barcode,
        "Descrição": expiredProduct.product.name,
        "Fabricante": expiredProduct.product.manufacturer,
        "Grupo": expiredProduct.product.group,
        "Quantidade": expiredProduct.quantity,
        "Lote": expiredProduct.lot,
        "Data validade": TransformDateToBrazilianFormat(expiredProduct.expirationDate),
        "Dias a vencer": expiredProduct.daysToExpire,
        "Vendido": expiredProduct.quantitySold,
        "Perdido": expiredProduct.quantityLost,
        "Saldo": expiredProduct.balance,
        "Localização": expiredProduct.location,
        "Colaborador": expiredProduct.user.name,
      }));
      const worksheet = XLSX.utils.json_to_sheet(formattedProducts);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Produtos");
      const excelData = XLSX.write(workbook, { type: "array", bookType: "xlsx" });
      const blob = new Blob([excelData], { type: "application/octet-stream" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Produtos_${storeName}.xlsx`;
      link.click();
      console.log(`Planilha gerada com sucesso para a loja: ${storeName}`);
    } catch (error) {
      console.error("Erro ao gerar a planilha:", error);
      toast.error("Erro ao gerar a planilha. Tente novamente.");
    }
  };

  const isControleValidadeRoute = window.location.pathname === ADMIN_EXPIRY_DATE_CONTROL_ROUTE;
  return (
    <div className={styles.modalBackground}>
      <ToastContainer/>
      <div className={styles.modalContainer}>
        <div className={styles.content}>
          <div className={styles.imageContainer}>
            <img src={CloseModalIcon} onClick={closeModal} alt="Fechar Modal" />
          </div>
          <h2>Exportar Planilha de Produtos</h2>
          <div className={styles.inputContainer}>
            <label>Selecione a loja:</label>
            <div
              className={styles.selectWrapper}
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <div className={styles.dropdownHeader}>
                <span id={styles.selectStores}>
                  {selectedStoreState ? selectedStoreState.name : "Selecionar loja"}
                </span>
                <span className={styles.dropdownArrow}>▼</span>
              </div>
              {dropdownOpen && (
                <div className={styles.dropdownList}>
                  {stores.map((store) => (
                    <div className={styles.radioItem} key={store.id}>
                      <input
                        type="radio"
                        id={store.id}
                        name="storeSelection"
                        checked={selectedStoreState?.id === store.id}
                        onChange={() => handleStoreSelection(store)}
                      />
                      <label htmlFor={store.id}>{store.name}</label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {isProcessing && (
          <div className={styles.spinnerContainer}>
            <div className={styles.spinner}></div>
          </div>
        )}
        </div>
        <div className={styles.buttonContent}>
        <button
            className={styles.buttonConfirm}
            onClick={ onExport }>
            Exportar Produtos
          </button>
          <button onClick={closeModal} className={styles.buttonCancel} disabled={isProcessing}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

export default Modal;
